import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Row, Col, Statistic, Spin, Alert, Typography, Space, Avatar, List, Tag, Tooltip, Button, message } from 'antd';
import { UserOutlined, FileTextOutlined, TagOutlined, DollarOutlined, ArrowUpOutlined, ArrowDownOutlined, CopyOutlined, AppleOutlined, AndroidOutlined, DesktopOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend as RechartsLegend, ResponsiveContainer } from 'recharts';
import { Column } from '@ant-design/plots'; // Changed from Line to Column
import moment from 'moment';

import { getDashboardSummary, getUserGrowthStats } from '../../services/dashboard.service';
import { RootState } from '../../app/store';
import './Home.scss';

const { Title, Text } = Typography;

// Define interfaces for API response data
interface CategoryId {
  _id: string;
  name: string;
  color?: string;
}

interface UserInfo {
  _id: string;
  username?: string;
  email?: string;
  displayName?: string;
  avatar?: string;
}

interface RecentExpense {
  _id: string;
  amount: number;
  description: string;
  categoryId?: CategoryId;
  userId?: UserInfo;
  createdAt: string;
  updateAt: string;
  updatedAt?: string;
  currency?: string;
  transactionType?: string; // Add transaction type
}

interface RecentUser {
  _id: string;
  username: string;
  email: string;
  displayName?: string;
  avatar?: string;
  createAt: string;
  updateAt: string;
  lastLogin: string;
  activity: string;
  deviceOS?: string;
  appVersion?: string;
}

interface UserStats {
  currentMonthUsers: number;
  previousMonthUsers: number;
  userGrowth: number;
}

interface DeviceStats {
  ios: number;
  android: number;
  other: number;
}

interface DashboardSummary {
  totalUsers: number;
  totalExpenses: number;
  totalCategories: number;
  totalAmount: number;
  recentUsers: RecentUser[];
  recentExpenses: RecentExpense[];
  userStats: UserStats;
  deviceStats?: DeviceStats;
}

interface MonthlyGrowth {
  month: number;
  monthName: string;
  count: number;
}

interface DailyGrowth {
  date: string;
  dayName: string;
  count: number;
}

interface GrowthData {
  year: number;
  monthlyGrowth: MonthlyGrowth[];
  dailyGrowthLast30Days: DailyGrowth[];
}

const HomePage = () => {
  const { token } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<DashboardSummary | null>(null);
  const [growthData, setGrowthData] = useState<GrowthData | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get summary data
        const summaryResponse = await getDashboardSummary(token);
        if (summaryResponse.success) {
          setSummaryData(summaryResponse.data);
        } else {
          setError(summaryResponse.message || "Failed to fetch dashboard summary");
        }

        // Get user growth data
        const growthResponse = await getUserGrowthStats(token);
        if (growthResponse.success) {
          setGrowthData(growthResponse.data);
        } else {
          setError(prev => prev || growthResponse.message || "Failed to fetch growth statistics");
        }
      } catch (err) {
        console.error("Dashboard data fetch error:", err);
        setError("Failed to load dashboard data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="Loading dashboard data..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  // Prepare the chart data
  const userGrowthChartData = growthData?.monthlyGrowth.map(item => ({
    month: item.monthName,
    users: item.count
  })) || [];

  // Prepare daily growth chart data
  const dailyGrowthChartData = growthData?.dailyGrowthLast30Days.map(item => ({
    date: moment(item.date).format('DD'),
    day: item.dayName,
    users: item.count,
    fullDate: item.date
  })) || [];

  // Prepare device stats chart data
  const deviceStatsData = summaryData?.deviceStats
    ? [
        { type: 'iOS', value: summaryData.deviceStats.ios },
        { type: 'Android', value: summaryData.deviceStats.android },
        { type: 'Other', value: summaryData.deviceStats.other },
      ]
    : [];

  // Debug: Log deviceStatsData to check for runtime/data issues
  console.log('DeviceStatsData:', deviceStatsData, 'Raw:', summaryData?.deviceStats);

  // Config for the Column (Bar) chart with gradient colors
  const chartConfig = {
    data: userGrowthChartData,
    xField: 'month',
    yField: 'users',
    color: ({ users }: any) => {
      // Color gradient based on user count
      if (users === 0) return '#d9d9d9';
      if (users <= 5) return '#52c41a';
      if (users <= 10) return '#1890ff';
      if (users <= 15) return '#722ed1';
      return '#f5222d';
    },
    columnStyle: {
      radius: [4, 4, 0, 0],
      cursor: 'pointer',
    },
    label: {
      position: 'top',
      style: {
        fill: '#666',
        fontSize: 12,
        fontWeight: 'bold',
      },
      formatter: (datum: any) => {
        return datum.users > 0 ? `${datum.users}` : '';
      },
    },
    xAxis: {
      label: {
        autoRotate: false,
        style: {
          fontSize: 11,
        },
      },
    },
    yAxis: {
      label: {
        formatter: (v: string) => `${v}`,
      },
      grid: {
        line: {
          style: {
            stroke: '#f0f0f0',
            lineWidth: 1,
          },
        },
      },
    },
    meta: {
      users: {
        alias: 'New Users',
      },
    },
    tooltip: {
      showTitle: true,
      title: (title: string, datum: any) => {
        return datum?.month || title || 'Month';
      },
      formatter: (datum: any) => {
        return {
          name: 'New Users',
          value: `${datum?.users || 0} users`,
        };
      },
      customContent: (title: string, items: any[]) => {
        if (!items || items.length === 0) return '';

        const item = items[0];
        const data = item?.data;

        return `
          <div style="padding: 8px 12px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
            <div style="font-weight: bold; margin-bottom: 4px;">${data?.month || 'Month'}</div>
            <div style="color: #1890ff;">
              <span style="display: inline-block; width: 8px; height: 8px; background: #1890ff; border-radius: 50%; margin-right: 6px;"></span>
              New Users: <strong>${data?.users || 0}</strong>
            </div>
          </div>
        `;
      },
    },
    animation: {
      appear: {
        animation: 'scale-in-y',
        duration: 1000,
      },
    },
  };

  // Config for daily growth chart
  const dailyChartConfig = {
    data: dailyGrowthChartData,
    xField: 'date',
    yField: 'users',
    color: ({ users }: any) => {
      if (users === 0) return '#d9d9d9';
      if (users <= 2) return '#52c41a';
      if (users <= 5) return '#1890ff';
      if (users <= 8) return '#722ed1';
      return '#f5222d';
    },
    columnStyle: {
      radius: [2, 2, 0, 0],
      cursor: 'pointer',
    },
    label: {
      position: 'top',
      style: {
        fill: '#666',
        fontSize: 10,
        fontWeight: 'bold',
      },
      formatter: (datum: any) => {
        return datum.users > 0 ? `${datum.users}` : '';
      },
    },
    xAxis: {
      label: {
        autoRotate: true,
        style: {
          fontSize: 10,
        },
      },
    },
    yAxis: {
      label: {
        formatter: (v: string) => `${v}`,
      },
      grid: {
        line: {
          style: {
            stroke: '#f0f0f0',
            lineWidth: 1,
          },
        },
      },
    },
    meta: {
      users: {
        alias: 'New Users',
      },
    },
    tooltip: {
      showTitle: true,
      title: (title: string, datum: any) => {
        return moment(datum?.fullDate).format('MMMM DD, YYYY') || title || 'Date';
      },
      formatter: (datum: any) => {
        return {
          name: 'New Users',
          value: `${datum?.users || 0} users`,
        };
      },
      customContent: (title: string, items: any[]) => {
        if (!items || items.length === 0) return '';

        const item = items[0];
        const data = item?.data;

        return `
          <div style="padding: 8px 12px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
            <div style="font-weight: bold; margin-bottom: 4px;">${moment(data?.fullDate).format('MMMM DD, YYYY')} (${data?.day})</div>
            <div style="color: #1890ff;">
              <span style="display: inline-block; width: 8px; height: 8px; background: #1890ff; border-radius: 50%; margin-right: 6px;"></span>
              New Users: <strong>${data?.users || 0}</strong>
            </div>
          </div>
        `;
      },
    },
    animation: {
      appear: {
        animation: 'scale-in-y',
        duration: 800,
      },
    },
  };

  // Get current month name and data
  const getCurrentMonthInfo = () => {
    if (!growthData?.monthlyGrowth) return { name: 'Unknown', count: 0 };

    const currentMonth = new Date().getMonth() + 1;
    const currentMonthData = growthData.monthlyGrowth.find(item => item.month === currentMonth);
    return {
      name: currentMonthData?.monthName || 'Unknown',
      count: currentMonthData?.count || 0
    };
  };

  // Get today's user registration count from daily growth data
  const getTodayUserCount = () => {
    if (!growthData?.dailyGrowthLast30Days) return 0;

    const today = new Date().toLocaleDateString('en-CA');
    const todayData = growthData.dailyGrowthLast30Days.find(item => item.date === today);
    return todayData?.count || 0;
  };

  // Calculate growth percentage for today vs yesterday
  const getTodayGrowthPercentage = () => {
    if (!growthData?.dailyGrowthLast30Days) return null;

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    const todayData = growthData.dailyGrowthLast30Days.find(item => item.date === todayStr);
    const yesterdayData = growthData.dailyGrowthLast30Days.find(item => item.date === yesterdayStr);

    const todayCount = todayData?.count || 0;
    const yesterdayCount = yesterdayData?.count || 0;

    if (yesterdayCount === 0) return todayCount > 0 ? 100 : 0;

    return Math.round(((todayCount - yesterdayCount) / yesterdayCount) * 100);
  };

  const currentMonthInfo = getCurrentMonthInfo();
  const todayUserCount = getTodayUserCount();
  const todayGrowthPercentage = getTodayGrowthPercentage();

  // Add this helper function to copy text to clipboard
  const copyToClipboard = (text: string, label: string = 'ID') => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copied to clipboard!`);
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  // Helper function to get device icon
  const getDeviceIcon = (deviceOS?: string) => {
    if (!deviceOS) return <DesktopOutlined style={{ color: '#999' }} />;
    
    const os = deviceOS.toLowerCase();
    if (os.includes('ios') || os.includes('iphone') || os.includes('ipad')) {
      return <AppleOutlined style={{ color: '#666' }} />;
    } else if (os.includes('android')) {
      return <AndroidOutlined style={{ color: '#52c41a' }} />;
    } else {
      return <DesktopOutlined style={{ color: '#999' }} />;
    }
  };

  // Helper function to format color for Tag component (same as in ExepensePage)
  const formatTagColor = (color) => {
    if (!color) return "#108ee9";

    // Remove # if present
    const cleanColor = color.replace('#', '');

    // If it's 8 characters, remove the first 2 characters (FF) and keep the last 6
    if (cleanColor.length === 8) {
      return `#${cleanColor.substring(2)}`;
    }

    // If it's already 6 characters, return as is
    if (cleanColor.length === 6) {
      return `#${cleanColor}`;
    }

    // If it's 3 characters, expand it (e.g., "F00" -> "FF0000")
    if (cleanColor.length === 3) {
      return `#${cleanColor.split('').map(char => char + char).join('')}`;
    }

    // Default fallback
    return "#108ee9";
  };

  // Helper function to format currency as Vietnamese Dong
  const formatCurrency = (amount, currency = 'VND') => {
    if (!amount && amount !== 0) return '0 ₫';

    // Convert to number if it's a string
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (currency === 'VND' || !currency) {
      // Format as Vietnamese Dong
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numAmount);
    } else if (currency === 'USD') {
      // Format as US Dollar
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(numAmount);
    } else {
      // Default formatting with currency symbol
      return `${numAmount.toLocaleString()} ${currency}`;
    }
  };

  // Helper function to get transaction color and prefix
  const getTransactionStyle = (transactionType, amount) => {
    const isExpense = transactionType === 'expense' || transactionType === 'Expense';
    const isIncome = transactionType === 'income' || transactionType === 'Income';

    if (isExpense) {
      return {
        color: '#d32f2f', // More vibrant red for expenses
        prefix: '-'
      };
    } else if (isIncome) {
      return {
        color: '#2e7d32', // More vibrant green for income
        prefix: '+'
      };
    } else {
      // Default color if transaction type is not specified
      return {
        color: '#d32f2f', // Default to vibrant red (assuming expense)
        prefix: ''
      };
    }
  };

  // Add this helper function to get user initials (same as in AccountPage)
  const getUserInitials = (username, displayName) => {
    if (displayName && displayName.trim()) {
      const names = displayName.trim().split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return displayName[0].toUpperCase();
    }

    if (username && username.trim()) {
      return username[0].toUpperCase();
    }

    return 'U';
  };

  return (
    <div className="dashboard-container">
      <Title level={2} className="dashboard-title">Admin Dashboard</Title>

      {/* User Statistics Row */}
      <Row gutter={[16, 16]} className="summary-stats">
        <Col xs={24} sm={8} lg={8}>
          <Card>
            <Statistic
              title="Total Users"
              value={summaryData?.totalUsers || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              All registered users
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={8}>
          <Card>
            <Statistic
              title="Users This Month"
              value={currentMonthInfo.count}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix={
                currentMonthInfo.count > 0 ? (
                  <Text type="success" className="growth-indicator">
                    <ArrowUpOutlined />
                  </Text>
                ) : null
              }
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {currentMonthInfo.name} registrations
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={8}>
          <Card>
            <Statistic
              title="Users Today"
              value={todayUserCount}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#fa8c16' }}
              suffix={
                todayGrowthPercentage !== null && todayGrowthPercentage !== 0 ? (
                  <Text
                    type={todayGrowthPercentage > 0 ? "success" : "danger"}
                    className="growth-indicator"
                  >
                    {todayGrowthPercentage > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {Math.abs(todayGrowthPercentage)}%
                  </Text>
                ) : null
              }
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              New registrations today
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Other Statistics Row */}
      <Row gutter={[16, 16]} className="summary-stats">
        <Col xs={24} sm={8} lg={8}>
          <Card>
            <Statistic
              title="Total Expenses"
              value={summaryData?.totalExpenses || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={8}>
          <Card>
            <Statistic
              title="Total Categories"
              value={summaryData?.totalCategories || 0}
              prefix={<TagOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={8}>
          <Card>
            <Statistic
              title="Total Amount"
              value={(summaryData?.totalAmount || 0).toLocaleString()}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Growth Chart */}
      <Row gutter={[16, 16]} className="dashboard-charts">
        <Col xs={24} lg={8}>
          <Card title="Device Distribution" className="chart-card">
            {Array.isArray(deviceStatsData) && deviceStatsData.length > 0 && deviceStatsData.some(d => typeof d.value === 'number' && d.value > 0) ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={deviceStatsData}
                    dataKey="value"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, value, name }) => {
                      if (!value || value <= 0) return null;
                      // Tính vị trí label
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 1.15;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#333"
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          fontSize={13}
                          fontWeight={600}
                          style={{ pointerEvents: 'none' }}
                        >
                          {`${name}: ${value}`}
                        </text>
                      );
                    }}
                    labelLine={false}
                  >
                    {deviceStatsData.map((entry, idx) => {
                      let color = '#faad14';
                      if (entry.type === 'iOS') color = '#1890ff';
                      else if (entry.type === 'Android') color = '#52c41a';
                      return <Cell key={`cell-${idx}`} fill={color} />;
                    })}
                  </Pie>
                  <RechartsTooltip formatter={(value, name, props) => [`${value} devices`, name]} />
                  <RechartsLegend verticalAlign="bottom" height={36} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', color: '#999', padding: '32px 0' }}>
                No device data
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="User Growth (This Year)" className="chart-card">
            <Column {...chartConfig} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Daily User Growth (Last 30 Days)" className="chart-card">
            <Column {...dailyChartConfig} />
          </Card>
        </Col>
      </Row>

      {/* Recent Data */}
      <Row gutter={[16, 16]} className="recent-data">
        <Col xs={24} md={12}>
          <Card title="Recent Users" className="recent-users-card">
            <List
              size="small"
              dataSource={summaryData?.recentUsers || []}
              renderItem={(user: RecentUser) => {
                const initials = getUserInitials(user.username, user.displayName);

                // Generate a consistent color based on the user's name
                const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#108ee9', '#f50', '#2db7f5', '#52c41a', '#eb2f96'];
                const colorIndex = (user.username || user.email || '').charCodeAt(0) % colors.length;

                return (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        user.avatar && user.avatar.trim() ? (
                          <Avatar
                            size={40}
                            src={user.avatar}
                            alt={user.displayName || user.username}
                            style={{
                              border: '2px solid #f0f0f0',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {initials}
                          </Avatar>
                        ) : (
                          <Avatar
                            size={40}
                            style={{
                              backgroundColor: colors[colorIndex],
                              color: 'white',
                              fontWeight: 'bold',
                              border: '2px solid #f0f0f0',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {initials}
                          </Avatar>
                        )
                      }
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: '500', fontSize: '14px' }}>
                            {user.displayName || user.username}
                          </span>
                          {user.deviceOS && (
                            <Tooltip title={user.deviceOS}>
                              {getDeviceIcon(user.deviceOS)}
                            </Tooltip>
                          )}
                          {user.appVersion && (
                            <Tag color="blue" style={{ fontSize: '11px', margin: 0 }}>
                              v{user.appVersion}
                            </Tag>
                          )}
                        </div>
                      }
                      description={
                        <div>
                          <div style={{ color: '#666', fontSize: '13px', marginBottom: '2px' }}>
                            {user.email}
                          </div>
                          <div style={{ color: '#999', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                            <span>ID:</span>
                            <code style={{ 
                              background: '#f5f5f5', 
                              padding: '2px 6px', 
                              borderRadius: '3px',
                              fontSize: '11px',
                              fontFamily: 'monospace'
                            }}>
                              {user._id.substring(0, 8)}...
                            </code>
                            <Tooltip title="Copy full ID">
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => copyToClipboard(user._id, 'User ID')}
                                style={{ 
                                  padding: '0 4px',
                                  height: '20px',
                                  fontSize: '12px'
                                }}
                              />
                            </Tooltip>
                          </div>
                          <div style={{ color: '#999', fontSize: '12px' }}>
                            Activity: {user.activity ? moment(user.activity).fromNow() : 'Never'}
                          </div>
                          <div style={{ color: '#999', fontSize: '12px' }}>
                            CreatedAt: {user.createAt ? moment(user.createAt).format("HH:mm DD/MM/yyyy") : 'Never'}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Recent Expenses" className="recent-expenses-card">
            <List
              size="small"
              dataSource={summaryData?.recentExpenses || []}
              renderItem={(expense: RecentExpense) => {
                const transactionStyle = getTransactionStyle(expense.transactionType, expense.amount);
                const userInitials = getUserInitials(expense.userId?.username, expense.userId?.displayName);

                // Generate a consistent color for user avatar
                const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#108ee9', '#f50', '#2db7f5', '#52c41a', '#eb2f96'];
                const colorIndex = (expense.userId?.username || expense.userId?.email || '').charCodeAt(0) % colors.length;

                return (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <span
                            style={{
                              fontWeight: 'bold',
                              color: transactionStyle.color,
                              fontSize: '15px',
                              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}
                          >
                            {transactionStyle.prefix}{formatCurrency(expense.amount, expense.currency)}
                          </span>
                          {expense.categoryId && (
                            <Tag color={formatTagColor(expense.categoryId.color)}>
                              {expense.categoryId.name}
                            </Tag>
                          )}
                          {expense.transactionType && (
                            <Tag
                              color={expense.transactionType === 'expense' || expense.transactionType === 'Expense' ? '#d32f2f' : '#2e7d32'}
                              style={{
                                textTransform: 'capitalize',
                                color: 'white',
                                fontWeight: 'bold',
                                border: 'none'
                              }}
                            >
                              {expense.transactionType}
                            </Tag>
                          )}
                        </Space>
                      }
                      description={
                        <>
                          <div style={{ marginBottom: '6px', fontWeight: '500' }}>
                            {expense.description}
                          </div>
                          <div style={{ fontSize: '12px', color: '#999', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>By</span>
                            {expense.userId?.avatar && expense.userId.avatar.trim() ? (
                              <Avatar
                                size={20}
                                src={expense.userId.avatar}
                                alt={expense.userId.displayName || expense.userId.username}
                                style={{
                                  border: '1px solid #f0f0f0',
                                  flexShrink: 0
                                }}
                              >
                                {userInitials}
                              </Avatar>
                            ) : (
                              <Avatar
                                size={20}
                                style={{
                                  backgroundColor: colors[colorIndex],
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '10px',
                                  border: '1px solid #f0f0f0',
                                  flexShrink: 0
                                }}
                              >
                                {userInitials}
                              </Avatar>
                            )}
                            <span style={{ fontWeight: '500', color: '#666' }}>
                              {expense.userId?.displayName || expense.userId?.username}
                            </span>
                            <span>·</span>
                            <span>{moment(expense.updatedAt || expense.updateAt).fromNow()}</span>
                          </div>
                        </>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HomePage;
